const DEFAULT_SPREAD = 0;

export function buildRollOverGroupedData(selectedRows, options = {}) {
  const {
    getTradingSymbol = (row) => row.exchange_trading_symbol || row.trading_symbol || "Unknown",
    getAccountCode = (row) => row.broker_client_code,
    getDefaultQuantity = (row) => Math.abs(row.net_quantity ?? row.quantity ?? 0),
    getRowKey = (row, index, symbol) =>
      row.key || `${symbol}_${getAccountCode(row) || "account"}_${index}`,
    defaultSpread = DEFAULT_SPREAD,
  } = options;

  if (!Array.isArray(selectedRows) || selectedRows.length === 0) {
    return {};
  }

  const grouped = {};

  selectedRows.forEach((row, index) => {
    const symbol = getTradingSymbol(row);

    if (!grouped[symbol]) {
      grouped[symbol] = {
        rows: [],
        rollOverTradingSymbol: symbol,
      };
    }

    const quantity = getDefaultQuantity(row);
    const rowIndexInGroup = grouped[symbol].rows.length;

    grouped[symbol].rows.push({
      ...row,
      modalId: getRowKey(row, index, symbol),
      quantity,
      maxQuantity: quantity,
      spread: defaultSpread,
      spreadOrderType: "Bid-Ask%",
      marginCheck: rowIndexInGroup === 0,
    });
  });

  return grouped;
}

export function collectRollOverModalIds(groupedData) {
  return Object.values(groupedData).flatMap((group) =>
    (group.rows || []).map((row) => row.modalId)
  );
}

export function buildRollOverConfirmPayload(groupedData, rowData, symbolOverrides = {}) {
  const groups = {};

  Object.entries(groupedData).forEach(([symbol, groupData]) => {
    const rollOverTradingSymbol = symbolOverrides[symbol] ?? symbol;

    groups[symbol] = {
      rollOverTradingSymbol,
      rows: (groupData.rows || []).map((row) => {
        const edits = rowData[row.modalId] || {};
        return {
          ...row,
          rollOverTradingSymbol,
          quantity: edits.quantity ?? row.quantity,
          maxQuantity: edits.maxQuantity ?? row.maxQuantity,
          spread: edits.spread ?? row.spread,
          spreadOrderType: edits.spreadOrderType ?? row.spreadOrderType,
          marginCheck: edits.marginCheck ?? row.marginCheck,
        };
      }),
    };
  });

  return { groups };
}

function deriveRollOverTransactionType(netQuantity) {
  const qty = Number(netQuantity);
  if (qty < 0) return "Buy";
  return "Sell";
}

function toPositiveNumber(value, fallback = 0) {
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.abs(parsed);
}

export function buildSpreadWindowOrderPayload(rollOverPayload) {
  const { groups } = rollOverPayload || {};
  if (!groups || typeof groups !== "object") {
    return [];
  }

  const orders = [];

  Object.entries(groups).forEach(([nearSymbol, groupData]) => {
    const farSymbol = groupData.rollOverTradingSymbol ?? nearSymbol;

    (groupData.rows || []).forEach((row) => {
      orders.push({
        broker_client_code: row.broker_client_code ?? "",
        exchange: row.exchange ?? "NSE",
        near_trading_symbol: nearSymbol,
        far_trading_symbol: farSymbol,
        transaction_type: deriveRollOverTransactionType(row.net_quantity),
        spread: toPositiveNumber(row.spread, 0),
        order_quantity: toPositiveNumber(row.quantity, 0),
        max_quantity: toPositiveNumber(row.maxQuantity, 0),
        margin_check: Boolean(row.marginCheck),
      });
    });
  });

  return orders;
}

export { DEFAULT_SPREAD };
